import React, { useCallback, useState } from 'react';

import { Box, ClickAwayListener, IconButton } from '@mui/material';
import { AlineTooltip } from 'src/components/generic';
import { AIReasoningIcon } from 'src/components/icons/AIReasoningIcon';
import { IconColors } from 'src/components/icons/icon-utils';

import { RelationshipReasonMarkdown } from './RelationshipReasonMarkdown';

interface AiReasoningTooltipButtonProps {
  /** Markdown-formatted reason with inline citation links. */
  llmReasonsMarkdown: string | null | undefined;
  /** Tooltip content when there are no reasons; without it the button renders nothing. */
  emptyTitle?: string;
  /** Threaded down to the inline citations so a cross-document jump can highlight this row. */
  relationshipId?: string | null;
  size?: 'small' | 'medium';
}

/**
 * The brain icon on a relationship row. Hover previews the AI reasoning; clicking pins the
 * tooltip open (blue icon, selected background) so its citations are easy to click, until the icon is clicked
 * again, the user clicks elsewhere, or Escape.
 */
export function AiReasoningTooltipButton({
  emptyTitle,
  llmReasonsMarkdown,
  relationshipId,
  size = 'small',
}: AiReasoningTooltipButtonProps): JSX.Element | null {
  const [pinned, setPinned] = useState(false);
  const [hoverOpen, setHoverOpen] = useState(false);

  const handleHoverOpen = useCallback(() => setHoverOpen(true), []);
  const handleHoverClose = useCallback((event: React.SyntheticEvent | Event) => {
    setHoverOpen(false);
    // Tooltip's onClose receives the native KeyboardEvent for Escape; a pinned popup must stay dismissible.
    if (event instanceof KeyboardEvent && event.key === 'Escape') setPinned(false);
  }, []);

  const handleToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setPinned(prev => !prev);
    // Tooltip's delayed onOpen can land after the click; drop hover state so unpinning always closes.
    setHoverOpen(false);
  }, []);

  /**
   * ClickAwayListener treats clicks anywhere in the React tree as "inside," including
   * the portaled tooltip content, so clicking within the tooltip does not unpin. It must
   * listen on mousedown: most relationship-row controls stopPropagation on click, which
   * kills the native event before it reaches ClickAwayListener's document listener.
   */
  const handleClickAway = useCallback(() => setPinned(false), []);

  if (!llmReasonsMarkdown && !emptyTitle) return null;

  return (
    <ClickAwayListener mouseEvent="onMouseDown" onClickAway={handleClickAway}>
      <span>
        <AlineTooltip
          arrow
          disableInteractive={false}
          open={pinned || hoverOpen}
          placement="top"
          slotProps={{ tooltip: { sx: { maxWidth: 420, px: 2, py: 1 } } }}
          title={
            llmReasonsMarkdown ? (
              <RelationshipReasonMarkdown
                llmReasonsMarkdown={llmReasonsMarkdown}
                relationshipId={relationshipId}
              />
            ) : (
              emptyTitle
            )
          }
          onClose={handleHoverClose}
          onOpen={handleHoverOpen}
        >
          <IconButton
            aria-label="AI reasoning"
            aria-pressed={pinned}
            size={size}
            sx={theme => ({
              '&:hover': {
                backgroundColor: pinned ? theme.appColors.selectedBackground : undefined,
              },
              backgroundColor: pinned ? theme.appColors.selectedBackground : undefined,
            })}
            onClick={handleToggle}
          >
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
              }}
            >
              <AIReasoningIcon
                height={18}
                stroke={pinned ? IconColors.Blue : undefined}
                width={18}
              />
            </Box>
          </IconButton>
        </AlineTooltip>
      </span>
    </ClickAwayListener>
  );
}
